"use client";

import { createContext, useContext, useCallback } from "react";
import type { Locale, Dict } from "./locale-utils";
import { localeHref } from "./locale-utils";

interface LocaleContextValue {
  locale: Locale;
  dict: Dict;
  t: (key: string, params?: Record<string, string>) => string;
  // Ссылка с учётом текущей локали (en → префикс /en).
  href: (path: string) => string;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({
  locale,
  dict,
  children,
}: {
  locale: Locale;
  dict: Dict;
  children: React.ReactNode;
}) {
  const t = useCallback(
    (key: string, params?: Record<string, string>) => {
      let val = dict[key] ?? key;
      if (params) {
        for (const [k, v] of Object.entries(params)) {
          val = val.replaceAll(`{${k}}`, v);
        }
      }
      return val;
    },
    [dict],
  );
  const href = useCallback((path: string) => localeHref(path, locale), [locale]);

  return (
    <LocaleContext.Provider value={{ locale, dict, t, href }}>
      {children}
    </LocaleContext.Provider>
  );
}

// Не бросаем, если провайдера нет (напр. изолированный рендер) — дефолт ru.
export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) {
    return {
      locale: "ru",
      dict: {},
      t: (key: string) => key,
      href: (path: string) => path,
    };
  }
  return ctx;
}
